import { es } from "date-fns/locale";
import { useFormik } from "formik";
import { useAtom } from "jotai";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { PiMapPin, PiPath } from "react-icons/pi";
import { Input } from "rizzui";
import * as yup from 'yup';
import { formsSubmitAtom } from "../../../atom/atom";
import CustomSelect from "../../../components/forms/CustomSelect";
import { DatePicker } from "../../../components/forms/DatePicker";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import useHeadquarters from "../../../hooks/api-calls/useHeadquarters";
import useRoutes from "../../../hooks/api-calls/useRoutes";
import useUsers from "../../../hooks/api-calls/useUsers";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { PickupService } from "../../../services/pickup/pickupService";
import { Pickup, PickupFormValues, PickupType, RouteHeadquarterSchedule } from "../../../type/entities/pickup-type";

export interface PickupsFormProps {
    open: boolean;
    onClose: () => void;
    pickupSelected: Pickup | null;
    formRef?: any;
}



const schema = yup.object({
    // Validación condicional para conductor siempre requerido
    driverId: yup.string().required('El conductor es obligatorio'),
    pickupType: yup.string().oneOf(['route', 'individual']).required('Debe seleccionar el tipo de recogida'),
    // Validación condicional para sede individual
    headquarterId: yup.string().when('pickupType', {
        is: 'individual',
        then: (schema) => schema.required('La sede es obligatoria'),
        otherwise: (schema) => schema.notRequired()
    }),
    pickupDate: yup.date().when('pickupType', {
        is: 'individual',
        then: (schema) => schema.required('La fecha de recogida es obligatoria'),
        otherwise: (schema) => schema.notRequired()
    }),
    pickupTime: yup.string().when('pickupType', {
        is: 'individual',
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.notRequired()
    }),
    // Validación condicional para ruta
    routeId: yup.string().when('pickupType', {
        is: 'route',
        then: (schema) => schema.required('La ruta es obligatoria'),
        otherwise: (schema) => schema.notRequired()
    }),
    routeHeadquartersSchedules: yup.array().when('pickupType', {
        is: 'route',
        then: (schema) => schema.min(1, 'Debe programar al menos una sede').required(),
        otherwise: (schema) => schema.notRequired()
    })
})

const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('La compañía es obligatoria'),
}))

const PickupsForm: FC<PickupsFormProps> = ({
    open,
    onClose,
    pickupSelected,
    formRef
}) => {

    // STATES
    const isEdit = pickupSelected !== null;
    const service = new PickupService();

    const [routeHeadquarters, setRouteHeadquarters] = useState<any[]>([]);
    const [loadingRouteHeadquarters, setLoadingRouteHeadquarters] = useState(false);
    const [isSearched, setIsSearched] = useState<boolean>(false);
    const [lastCompanyId, setLastCompanyId] = useState<string>('');

    // HOOKS
    const [, setIsSubmitting] = useAtom(formsSubmitAtom);
    const { handleErrors } = useHandleErrors();
    const { getHeadquartersList, fetchHeadquarters, loadingHeadquarters } = useHeadquarters();
    const { getCompaniesList } = useCompanies();
    const { getRoutesList, loadingRoutes, fetchRoutes } = useRoutes();
    const { getUsersList, usersLoading, fetchUsers } = useUsers();
    const { userCan } = usePrivilege();

    // FORMIK
    const formik = useFormik<PickupFormValues>({
        initialValues: {
            id: pickupSelected?.id ?? '',
            pickupType: 'individual' as PickupType,
            driverId: pickupSelected?.user?.id ?? '',
            // Para sede individual
            headquarterId: pickupSelected?.headquarter?.id ?? '',
            pickupDate: pickupSelected?.pickupDate?.date ? moment(pickupSelected.pickupDate.date).format('YYYY-MM-DD') : '',
            pickupTime: pickupSelected?.pickupDate?.date ? moment(pickupSelected.pickupDate.date).format('HH:mm') : '',
            // Para ruta
            routeId: pickupSelected?.route?.id ?? '',
            routeHeadquartersSchedules: [] as RouteHeadquarterSchedule[],
            company: pickupSelected?.company?.id ?? '',
        },
        validationSchema: userCan('list', 'company') ? superAdminSchema : schema,
        validateOnBlur: false,
        onSubmit: values => {
            if (!isEdit) {
                handleCreate(values)
            } else {
                handleEdit(values)
            }
        },
    });

    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES Obtiene las sedes de una ruta seleccionada
     * @EN Gets headquarters from selected route
     */
    //-----------------------------------------------------------------------------------------------------------------
    const fetchRouteHeadquarters = async (routeId: string) => {
        if (!routeId) return;

        setLoadingRouteHeadquarters(true);
        try {
            const response = await service.getRouteHeadquarters(routeId);
            const data = response.getResponseData();

            if (data.success && data.data) {
                setRouteHeadquarters(data.data);
                // Inicializar los horarios para cada sede
                const initialSchedules = data.data.map((hq: any, index: number) => ({
                    id: hq.headquarter.id,
                    name: hq.headquarter.name,
                    pickupDate: moment().add(1, 'day').format('YYYY-MM-DD'),
                    pickupTime: extractTimeFromScheduleText(hq?.headquarter?.pickupSchedule)
                        ?? extractTimeFromScheduleText(hq?.headquarter?.schedules)
                        ?? `${10 + index}:00`,
                }));
                formik.setFieldValue('routeHeadquartersSchedules', initialSchedules);
            }
        } catch (error) {
            handleErrors(error);
            setRouteHeadquarters([]);
        } finally {
            setLoadingRouteHeadquarters(false);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES Obtiene las sedes de una ruta seleccionada para el selector múltiple
     * @EN Gets headquarters from selected route for multiple selection
     */
    //-----------------------------------------------------------------------------------------------------------------
    const getRoutesHeadquartersList = () => {
        return routeHeadquarters.map((hq: any) => ({ value: hq.headquarter.id, label: hq.headquarter.name }));
    }
    //-----------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES Obtiene las sedes de la compañía seleccionada para recogida individual
     * @EN Gets headquarters of the selected company for individual pickup
     */
    //-----------------------------------------------------------------------------------------------------------------
    const getIndividualHeadquartersList = () => {
        return getHeadquartersList();
    }
    //-----------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES Extrae una hora válida (HH:mm) desde texto de horarios
     * @EN Extracts a valid time (HH:mm) from schedule text
     */
    //-----------------------------------------------------------------------------------------------------------------
    const extractTimeFromScheduleText = (text?: string | null): string | null => {
        if (!text || typeof text !== 'string') {
            return null;
        }

        const hourMinuteMatch = text.match(/(\d{1,2}):(\d{2})/);
        if (hourMinuteMatch) {
            const hour = Number(hourMinuteMatch[1]);
            const minute = Number(hourMinuteMatch[2]);

            if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
                return null;
            }

            return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        }

        const standaloneHourMatch = text.match(/\b([01]?\d|2[0-3])\b/);
        if (!standaloneHourMatch) {
            return null;
        }

        const hour = Number(standaloneHourMatch[1]);
        if (!Number.isFinite(hour) || hour < 0 || hour > 23) {
            return null;
        }

        return `${String(hour).padStart(2, '0')}:00`;
    };
    //-----------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES Obtiene la hora por defecto de recogida para una sede
     * @EN Gets the default pickup time for a headquarter
     */
    //-----------------------------------------------------------------------------------------------------------------
    const getDefaultPickupTimeByHeadquarterId = (headquarterId: string): string | null => {
        if (!headquarterId) {
            return null;
        }

        const selectedHeadquarter = getIndividualHeadquartersList().find((headquarter: any) => headquarter.value === headquarterId);

        if (!selectedHeadquarter) {
            return null;
        }

        return (
            extractTimeFromScheduleText(selectedHeadquarter.pickupSchedule) ??
            extractTimeFromScheduleText(selectedHeadquarter.schedules)
        );
    };
    //-----------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES Actualiza la fecha y hora de una sede específica
     * @EN Updates date and time for specific headquarters
     */
    //-------------------------------------------------------------------------------------------------------------
    const updateHeadquarterSchedule = (headquarterId: string, field: 'pickupDate' | 'pickupTime', value: string) => {
        const currentSchedules = formik.values.routeHeadquartersSchedules;
        const updatedSchedules = currentSchedules.map(schedule =>
            schedule.id === headquarterId
                ? { ...schedule, [field]: value }
                : schedule
        );
        formik.setFieldValue('routeHeadquartersSchedules', updatedSchedules);
    };
    //-----------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UNA NUEVA RECOGIDA
     * @EN CREATE A NEW PICKUP
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: PickupFormValues) => {
        try {
            const individualPickupTime = values.pickupTime || getDefaultPickupTimeByHeadquarterId(values.headquarterId) || '09:00';
            const sendValues = {
                headquarters: values.pickupType === 'individual' ? [{
                    id: values.headquarterId,
                    pickupDate: moment(`${values.pickupDate} ${individualPickupTime}`, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm:ss')
                }] :
                    values.routeHeadquartersSchedules.map((schedule: RouteHeadquarterSchedule) => ({
                        id: schedule.id,
                        pickupDate: moment(`${schedule.pickupDate} ${schedule.pickupTime || '09:00'}`, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm:ss')
                    })),
                routeId: values.routeId,
                company: values.company,
                driverId: values.driverId,
            }
            const response = await (await service.createPickup(sendValues)).getResponseData();

            if (response.success) {
                formik.resetForm();
                onClose();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsSubmitting(false);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UNA RECOGIDA
     * @EN EDIT A PICKUP
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: PickupFormValues) => {
        try {
            const pickupTime = values.pickupTime || getDefaultPickupTimeByHeadquarterId(values.headquarterId) || '09:00';
            const pickupDateTime = moment(`${values.pickupDate} ${pickupTime}`, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm:ss');

            const editData = {
                id: values.id,
                headquarterId: values.headquarterId,
                pickupDate: pickupDateTime,
                company: values.company
            };

            const response = await (await service.editPickup(editData)).getResponseData();
            if (response.success) {
                formik.resetForm();
                onClose();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    // USE EFFECT
    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);

    // Efecto para cargar sedes cuando se selecciona una ruta
    useEffect(() => {
        if (formik.values.routeId) {
            fetchRouteHeadquarters(formik.values.routeId);
            return;
        }

        setRouteHeadquarters([]);
    }, [formik.values.routeId, formik.values.pickupType]);

    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0 && !isSearched) {
            setIsSearched(true);
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label === 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? '');
        }
    }, [getCompaniesList]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const selectedCompany = formik.values.company || null;

        if (lastCompanyId && selectedCompany !== lastCompanyId) {
            formik.setFieldValue('driverId', '');
            formik.setFieldValue('headquarterId', '');
            formik.setFieldValue('routeId', '');
            formik.setFieldValue('routeHeadquartersSchedules', []);
            setRouteHeadquarters([]);
        }

        setLastCompanyId(selectedCompany ?? '');

        if (selectedCompany) {
            fetchUsers({ limit: 500, filter_filters: { company: selectedCompany } });
            fetchRoutes({ company: selectedCompany });
            fetchHeadquarters({ company: selectedCompany });
            return;
        }

        fetchUsers({ limit: 500, filter_filters: {} });
        fetchRoutes({});
        fetchHeadquarters({});
    }, [formik.values.company]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const users = getUsersList();
        const routes = getRoutesList();
        const headquarters = getIndividualHeadquartersList();

        if (formik.values.driverId && !users.some((user: any) => user.value === formik.values.driverId)) {
            formik.setFieldValue('driverId', '');
        }

        if (formik.values.routeId && !routes.some((route: any) => route.value === formik.values.routeId)) {
            formik.setFieldValue('routeId', '');
            formik.setFieldValue('routeHeadquartersSchedules', []);
            setRouteHeadquarters([]);
        }

        if (formik.values.headquarterId && !headquarters.some((headquarter: any) => headquarter.value === formik.values.headquarterId)) {
            formik.setFieldValue('headquarterId', '');
        }
    }, [formik.values.company, routeHeadquarters, formik.values.driverId, formik.values.routeId, formik.values.headquarterId]);

    useEffect(() => {
        if (formik.values.pickupType !== 'individual' || !formik.values.headquarterId || formik.values.pickupTime) {
            return;
        }

        const defaultPickupTime = getDefaultPickupTimeByHeadquarterId(formik.values.headquarterId);
        if (defaultPickupTime) {
            formik.setFieldValue('pickupTime', defaultPickupTime);
        }
    }, [formik.values.headquarterId, formik.values.pickupType, formik.values.pickupTime, formik.values.company]);

    // RENDER
    return (
        <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                <div className={'md:col-span-12'}>
                    <div className='md:grid md:grid-cols-12 gap-4'>

                        {/* Selector de tipo de recogida */}
                        {!isEdit && (
                            <div className="md:col-span-12 mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Tipo de Recogida <span className="text-red-500">*</span>
                                </label>
                                <div className="grid grid-cols-2 gap-4">
                                    <div
                                        className={`col-span-2 md:col-span-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${formik.values.pickupType === 'individual'
                                            ? 'border-blue-500 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        onClick={() => {
                                            formik.setFieldValue('pickupType', 'individual');
                                            formik.setFieldValue('routeId', '');
                                            formik.setFieldValue('routeHeadquartersSchedules', []);
                                        }}
                                    >
                                        <div className="flex items-center space-x-3">
                                            <PiMapPin className="h-[2em] w-[2em] md:h-[4em] md:w-[4em] text-blue-600" />
                                            <div>
                                                <h3 className="font-medium text-gray-900">Sede Individual</h3>
                                                <p className="text-sm text-gray-500">Programar una recogida para una sede específica</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        className={`col-span-2 md:col-span-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${formik.values.pickupType === 'route'
                                            ? 'border-blue-500 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        onClick={() => {
                                            formik.setFieldValue('pickupType', 'route');
                                            formik.setFieldValue('headquarterId', '');
                                            formik.setFieldValue('pickupDate', '');
                                            formik.setFieldValue('pickupTime', '');
                                        }}
                                    >
                                        <div className="flex items-center space-x-3">
                                            <PiPath className="h-[2em] w-[2em] md:h-[4em] md:w-[4em] text-green-600" />
                                            <div>
                                                <h3 className="font-medium text-gray-900">Ruta Completa</h3>
                                                <p className="text-sm text-gray-500">Programar recogidas para todas las sedes de una ruta</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {formik.errors.pickupType && (
                                    <p className="text-red-500 text-sm mt-2">{formik.errors.pickupType}</p>
                                )}
                            </div>
                        )}

                        {/* Formulario para sede individual */}
                        {formik.values.pickupType === 'individual' && (
                            <>

                                <CustomSelect
                                    required
                                    isSearchable
                                    id={'driverId'}
                                    label="Conductor"
                                    containerClassName='md:col-span-12'
                                    value={{ value: formik.values.driverId, label: getUsersList()?.find((d: any) => d.value === formik.values.driverId)?.label }}
                                    options={getUsersList()}
                                    onChange={(e: any) => { formik.setFieldValue('driverId', e?.value) }}
                                    error={formik.errors.driverId}
                                />

                                <CustomSelect
                                    required
                                    isSearchable
                                    id={'headquarterId'}
                                    label="Centro de Distribución"
                                    isLoading={loadingHeadquarters || (loadingRouteHeadquarters && !!formik.values.routeId)}
                                    containerClassName='md:col-span-12 mt-2'
                                    value={{ value: formik.values.headquarterId, label: getIndividualHeadquartersList()?.find((g: any) => g.value === formik.values.headquarterId)?.label }}
                                    options={getIndividualHeadquartersList()}
                                    onChange={(e: any) => {
                                        formik.setFieldValue('headquarterId', e?.value);
                                        if (formik.values.pickupType === 'individual' && !formik.values.pickupTime) {
                                            const defaultPickupTime = getDefaultPickupTimeByHeadquarterId(e?.value ?? '');
                                            if (defaultPickupTime) {
                                                formik.setFieldValue('pickupTime', defaultPickupTime);
                                            }
                                        }
                                    }}
                                    error={formik.errors.headquarterId}
                                />

                                <CustomSelect
                                    required={false}
                                    isSearchable
                                    id={'routeId'}
                                    label="Ruta"
                                    containerClassName='md:col-span-12 mt-2'
                                    value={{ value: formik.values.routeId, label: getRoutesList()?.find((r: any) => r.value === formik.values.routeId)?.label }}
                                    options={getRoutesList()}
                                    onChange={(e: any) => { formik.setFieldValue('routeId', e?.value) }}
                                    error={formik.errors.routeId}
                                />

                                <div className="md:col-span-8 mt-2">
                                    <label htmlFor="pickupDate" className="block text-sm font-medium text-gray-700 mb-2">
                                        Fecha estimada de recogida <span className="text-red-500">*</span>
                                    </label>
                                    <DatePicker
                                        monthsShown={1}
                                        minDate={moment().add(1, 'day').toDate()}
                                        placeholderText="Selecciona una fecha"
                                        inputProps={{
                                            inputClassName: `h-9 [&_input]:text-ellipsis bg-white ${formik.errors.pickupDate ? 'border-red-500' : ''}`,
                                        }}
                                        className="col-span-12"
                                        selected={formik.values?.pickupDate ? moment(formik.values.pickupDate).toDate() : null}
                                        onChange={(date: any) => {
                                            formik.setFieldValue('pickupDate', date ? moment(date).format('YYYY-MM-DD') : '');
                                        }}
                                        locale={es}
                                        popperPlacement="bottom-start"

                                    />
                                    {formik.errors.pickupDate && (
                                        <p className="text-red-500 text-sm">{formik.errors.pickupDate?.toString()}</p>
                                    )}
                                </div>

                                <div className="md:col-span-4 mt-2">
                                    <label htmlFor="pickupTime" className="block text-sm font-medium text-gray-700 mb-2">
                                        Hora estimada
                                    </label>
                                    <div className="relative">
                                        <Input
                                            type="time"
                                            value={formik.values.pickupTime}
                                            onChange={(e) => formik.setFieldValue('pickupTime', e.target.value)}
                                            className={`${formik.errors.pickupTime ? 'border-red-500' : ''}`}
                                        />
                                    </div>
                                    {formik.errors.pickupTime && (
                                        <p className="text-red-500 text-sm">{formik.errors.pickupTime?.toString()}</p>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Formulario para ruta */}
                        {formik.values.pickupType === 'route' && (
                            <>
                                <CustomSelect
                                    required
                                    isSearchable
                                    id={'driverId'}
                                    label="Conductor"
                                    isLoading={usersLoading}
                                    containerClassName='md:col-span-12'
                                    value={{ value: formik.values.driverId, label: getUsersList()?.find((d: any) => d.value === formik.values.driverId)?.label }}
                                    options={getUsersList()}
                                    onChange={(e: any) => { formik.setFieldValue('driverId', e?.value) }}
                                    error={formik.errors.driverId}
                                />

                                <CustomSelect
                                    required
                                    isSearchable
                                    id={'routeId'}
                                    label="Ruta"
                                    isLoading={loadingRoutes}
                                    containerClassName='md:col-span-12'
                                    value={{ value: formik.values.routeId, label: getRoutesList()?.find((r: any) => r.value === formik.values.routeId)?.label }}
                                    options={getRoutesList()}
                                    onChange={(e: any) => {
                                        formik.setFieldValue('routeId', e?.value);
                                        formik.setFieldValue('routeHeadquartersSchedules', []);
                                    }}
                                    error={formik.errors.routeId}
                                />

                                {/* Sedes de la ruta */}
                                {formik.values.routeId && (
                                    <>
                                        <CustomSelect
                                            required
                                            isSearchable
                                            isMulti
                                            id={'routeHeadquartersSchedules'}
                                            label="Sedes"
                                            containerClassName='md:col-span-12'
                                            value={formik.values.routeHeadquartersSchedules?.map((hqs: RouteHeadquarterSchedule) => ({ value: hqs.id, label: hqs.name }))}
                                            options={getRoutesHeadquartersList().filter((h: any) => !formik.values.routeHeadquartersSchedules.includes(h.id))}
                                            onChange={(e: any) => {
                                                formik.setFieldValue('routeHeadquartersSchedules', e?.map((h: any) => ({
                                                    id: h.value,
                                                    name: h.label,
                                                    pickupDate: moment().add(1, 'day').format('YYYY-MM-DD'),
                                                    pickupTime: '09:00'
                                                })))
                                            }}
                                            error={formik.errors.routeHeadquartersSchedules}
                                        />

                                        {formik.values.routeHeadquartersSchedules?.length > 0 && (
                                            <div className="col-span-12 mt-6">
                                                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                                    <PiMapPin className="text-blue-600" />
                                                    Programación de Recogidas por Sede
                                                </h4>
                                                <div className="space-y-4 max-h-[80vh] overflow-y-auto pr-2">
                                                    {formik.values.routeHeadquartersSchedules?.map((hqs: RouteHeadquarterSchedule, index: number) => (
                                                        <div
                                                            key={hqs.id}
                                                            className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow duration-200"
                                                        >
                                                            <div className="flex items-center justify-between mb-3">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                                                                        {index + 1}
                                                                    </div>
                                                                    <h5 className="font-medium text-gray-800">{hqs.name}</h5>
                                                                </div>
                                                            </div>

                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                <div className="space-y-2">
                                                                    <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                                                                        Fecha de Recogida
                                                                    </label>
                                                                    <DatePicker
                                                                        value={hqs.pickupDate}
                                                                        onChange={(date: any) => {
                                                                            const formattedDate = moment(date).format('YYYY-MM-DD');
                                                                            updateHeadquarterSchedule(hqs.id, 'pickupDate', formattedDate);
                                                                        }}
                                                                        inputProps={{
                                                                            inputClassName: `h-9 [&_input]:text-ellipsis bg-white`,
                                                                        }}
                                                                        popperPlacement="bottom-start"
                                                                        className="col-span-12"
                                                                        placeholderText="Seleccionar fecha"
                                                                        dateFormat="dd/MM/yyyy"
                                                                        locale={es}
                                                                        minDate={moment().add(1, 'day').toDate()}
                                                                    />
                                                                </div>

                                                                <div className="space-y-2">
                                                                    <label className="block text-sm font-medium text-gray-700">
                                                                        Hora
                                                                    </label>
                                                                    <Input
                                                                        type="time"
                                                                        value={hqs.pickupTime || ''}
                                                                        onChange={(e) => updateHeadquarterSchedule(hqs.id, 'pickupTime', e.target.value)}
                                                                        className="w-full bg-white"
                                                                        placeholder="HH:MM"
                                                                    />
                                                                </div>
                                                            </div>

                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                                    <p className="text-sm text-blue-700 flex items-center gap-2">
                                                        <strong>Total de sedes programadas:</strong> {formik.values.routeHeadquartersSchedules?.length || 0}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                            </>
                        )}

                        {/* Campo de compañía para super admin */}
                        {userCan('list', 'company') && (
                            <CustomSelect
                            required
                            isSearchable
                            id={'company'}
                            label="Compañia"
                            containerClassName='md:col-span-12'
                            value={
                                formik.values.company ?
                                { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                getCompaniesList()?.find((g: any) => g.label === 'GESTION DE RESIDUOS 2023 SLU')
                            }
                            options={getCompaniesList()}
                            onChange={
                                (e: any) => { formik.setFieldValue('company', e?.value) }
                            }
                            error={formik.errors.company}
                            />
                        )}

                    </div>
                </div>
            </form>
        </div>
    )
}

export default PickupsForm;

